// CRM Sequences v3. Fixed two-pane layout: contact list on the left, one contact's
// full sequence on the right, each pane scrolls on its own. Bulk bar: generate
// (confirm, progress, stop), approve (chunked), add to batch, link tokens (email and
// LinkedIn), push to campaign. ManyReach removed.
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import FilterRail from '../../components/FilterRail';
import { SEQUENCE_SCHEMA } from '../../components/filterSchema';
import ConfirmActionModal from '../../components/ConfirmActionModal';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import MDEditor from '@uiw/react-md-editor';
import raptorIcon from '../../assets/raptor.svg';
import raptorMark from '../../assets/raptor-white.svg';

const PAGE_SIZE_DEFAULT = 50;
const GEN_CONCURRENCY = 10;
const LIVE_JOB = ['queued', 'running', 'stopping'];
const fmtDur = (sec) => (sec >= 60 ? `${Math.floor(sec / 60)}m ${sec % 60}s` : `${sec}s`);
function progressFromJob(j) {
  const finished = (j.done || 0) + (j.failed || 0) + (j.not_a_fit || 0);
  const startMs = j.started_at ? new Date(j.started_at).getTime() : Date.now();
  const endMs = j.finished_at ? new Date(j.finished_at).getTime() : Date.now();
  const elapsed = Math.max(0, Math.round((endMs - startMs) / 1000));
  const live = LIVE_JOB.includes(j.status);
  const left = live && finished ? Math.round((elapsed / finished) * (j.total - finished)) : null;
  const label = { done: 'Complete.', stopped: 'Stopped.', credits_out: 'Out of credits.', failed: 'Failed.' }[j.status];
  const parts = [`${label ? label + ' ' : ''}Generated ${j.done || 0} of ${j.total}${j.failed ? `, ${j.failed} failed` : ''}${j.not_a_fit ? `, ${j.not_a_fit} not a fit` : ''}${j.outside_icp ? `, ${j.outside_icp} outside core ICP (held for review)` : ''}`];
  parts.push(live ? `${fmtDur(elapsed)} elapsed` : `in ${fmtDur(elapsed)}`);
  if (left !== null && finished < j.total) parts.push(`about ${fmtDur(left)} left`);
  if (j.status === 'stopping') parts.push('stopping after the ones in progress');
  else if (live) parts.push('runs on the server, safe to close this tab');
  return { current: finished, total: j.total, status: parts.join(' \u00B7 ') };
}

const EMAIL_DESTINATIONS = [
  { value: 'pipedrive_csv', label: 'Pipedrive (CSV)' },
  { value: 'hubspot_csv', label: 'HubSpot (CSV)' },
  { value: 'bigin_csv', label: 'Zoho Bigin (CSV)' },
  { value: 'zoho_crm_csv', label: 'Zoho CRM (CSV)' },
  { value: 'salesforce_csv', label: 'Salesforce (CSV)' },
];

const TOKEN_ROWS_META = [
  { key: 'cta_url', label: '{{cta_url}}', sub: 'Booking or meeting link' },
  { key: 'landing_page', label: '{{landing_page}}', sub: 'Product, service or case study page' },
  { key: 'attachment', label: '{{attachment}}', sub: 'Document, deck or brochure' },
];

const EMAIL_STRATEGIES = [
  { value: 'end_sales', label: 'End of every sales-step email' },
  { value: 'end_all_emails', label: 'End of every email (sales and observation)' },
  { value: 'after_meeting_ask', label: 'After the meeting ask sentence (sales steps only)' },
];

const LI_STRATEGIES = [
  { value: 'end_sales_li', label: 'End of every sales-step LinkedIn message' },
  { value: 'end_all_li', label: 'End of every LinkedIn message' },
  { value: 'after_ask_li', label: 'After the meeting ask sentence (sales steps only)' },
];

const freshTokenRows = (strategy) => ({
  cta_url: { enabled: false, strategy },
  landing_page: { enabled: false, strategy },
  attachment: { enabled: false, strategy },
});

function avatarColour(name) {
  const palette = ['#5B3FD6','#0B5BB5','#056F4E','#B25400','#066576','#A21C5A','#3F4956','#7A4D00','#2E8FFF','#7C5CFC','#00A878','#C8202E','#8B5CF6','#0EA5E9','#D97706','#DB2777','#059669','#4F46E5'];
  let h = 0; for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) % palette.length;
  return palette[h];
}
function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}
function parseSteps(seq, channel) {
  if (!seq) return [];
  const raw = channel === 'email' ? seq.email_sequence : seq.linkedin_sequence;
  try { return (typeof raw === 'string' ? JSON.parse(raw) : raw) || []; } catch (_) { return []; }
}
// Quick view (status + time window + sort), remembered per client so leaving the screen
// and coming back lands on the same list.
const QV_DEFAULT = { status: 'all', when: 'any', from: '', to: '', basis: 'added', sort: 'newest' };
function readQuickView(clientId) {
  try { return { ...QV_DEFAULT, ...(JSON.parse(localStorage.getItem('sonar.seqView.' + clientId)) || {}) }; } catch (_) { return { ...QV_DEFAULT }; }
}
function windowFor(qv) {
  const now = Date.now();
  if (qv.when === '1h') return [now - 3600e3, null];
  if (qv.when === 'today') { const d = new Date(); d.setHours(0, 0, 0, 0); return [d.getTime(), null]; }
  if (qv.when === '24h') return [now - 86400e3, null];
  if (qv.when === '7d') return [now - 7 * 86400e3, null];
  if (qv.when === 'custom') return [qv.from ? new Date(qv.from).getTime() : null, qv.to ? new Date(qv.to).getTime() : null];
  return [null, null];
}
function shortWhen(v) {
  if (!v) return '';
  const d = new Date(v); if (isNaN(d)) return '';
  const t = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (d >= today) return t;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + t;
}

function chunk(arr, n) {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

export default function CrmSequences2() {
  const { clientId } = useOutletContext();
  const id = clientId;
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [allContacts, setAllContacts] = useState([]);
  const [allSequences, setAllSequences] = useState([]);
  const [allChannels, setAllChannels] = useState([]);
  const [ceCampaigns, setCeCampaigns] = useState([]);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [qv, setQv] = useState(() => readQuickView(id));
  useEffect(() => { setQv(readQuickView(id)); }, [id]);
  useEffect(() => { try { localStorage.setItem('sonar.seqView.' + id, JSON.stringify(qv)); } catch (_) {} }, [qv, id]);
  const setQ = (patch) => { setQv(v => ({ ...v, ...patch })); setPage(0); };
  const [seqTree, setSeqTree] = useState(null);
  const [seqIdSet, setSeqIdSet] = useState(null);
  const [seqFilterLoading, setSeqFilterLoading] = useState(false);
  const [seqViews, setSeqViews] = useState([]);
  const [seqTargets, setSeqTargets] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [activeViewId, setActiveViewId] = useState(null);
  const [saveModal, setSaveModal] = useState(false);
  const [viewName, setViewName] = useState('');
  const [savingView, setSavingView] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeContactId, setActiveContactId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('everyone');
  const [rapOpen, setRapOpen] = useState(() => {
    try { const v = localStorage.getItem('sonar.seqRaptorOpen'); if (v !== null) return v === '1'; } catch (_) {}
    return typeof window !== 'undefined' && window.innerWidth >= 1400;
  });
  const [rapThread, setRapThread] = useState([]);
  const [rapText, setRapText] = useState('');
  const [rapAsking, setRapAsking] = useState(false);
  const rapThreadRef = useRef(null);
  const rapContactRef = useRef(null);

  const [editingStep, setEditingStep] = useState(null);
  const [savingStep, setSavingStep] = useState(false);
  const [approvingChannel, setApprovingChannel] = useState(null);
  const textareaRef = useRef(null);
  const listRef = useRef(null);
  const rbodyRef = useRef(null);
  const menuRef = useRef(null);

  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [progress, setProgress] = useState(null);
  const activeJobRef = useRef(null);
  const pollKickRef = useRef(null);
  const pollTickRef = useRef(0);
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  const [pendingGenerateCount, setPendingGenerateCount] = useState(0);
  const [tokenMenu, setTokenMenu] = useState(false);

  const [batchOpen, setBatchOpen] = useState(false);
  const [batchChannel, setBatchChannel] = useState('linkedin');
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [creatingNewBatch, setCreatingNewBatch] = useState(false);
  const [newBatchName, setNewBatchName] = useState('');
  const [newBatchDestination, setNewBatchDestination] = useState('pipedrive_csv');
  const [batchSubmitting, setBatchSubmitting] = useState(false);

  const [tokenModal, setTokenModal] = useState(null);
  const [tokenAction, setTokenAction] = useState('insert');
  const [tokenRows, setTokenRows] = useState(freshTokenRows('end_sales'));
  const [tokenRunning, setTokenRunning] = useState(false);
  const [tokenResult, setTokenResult] = useState(null);
  const [tokenProgress, setTokenProgress] = useState('');

  const [campaignOpen, setCampaignOpen] = useState(false);
  const [ceCampaign, setCeCampaign] = useState('');
  const [pushingToCampaign, setPushingToCampaign] = useState(false);

  const authHeaders = useCallback(async (extra = {}) => {
    const token = await getToken();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...extra };
  }, [getToken]);

  const fetchAll = useCallback(async () => {
    try {
      const [contactsRes, seqsRes, channelsRes, campsRes] = await Promise.all([
        fetch(`${API_BASE}/clients/${id}/contacts`),
        fetch(`${API_BASE}/clients/${id}/sequences`),
        fetch(`${API_BASE}/clients/${id}/sequence-channels`),
        fetch(`${API_BASE}/clients/${id}/campaigns`),
      ]);
      const contacts = await contactsRes.json();
      setAllContacts(Array.isArray(contacts) ? contacts : []);
      const seqs = await seqsRes.json();
      setAllSequences(Array.isArray(seqs) ? seqs : []);
      const chans = await channelsRes.json();
      setAllChannels(Array.isArray(chans) ? chans : []);
      const camps = campsRes.ok ? await campsRes.json() : [];
      setCeCampaigns(Array.isArray(camps) ? camps : (camps.items || []));
    } catch (e) { setError('Failed to load.'); }
  }, [id]);

  const refreshSeqs = useCallback(async () => {
    const [seqs, chans] = await Promise.all([
      fetch(`${API_BASE}/clients/${id}/sequences`).then(r => r.json()),
      fetch(`${API_BASE}/clients/${id}/sequence-channels`).then(r => r.json()),
    ]);
    setAllSequences(Array.isArray(seqs) ? seqs : []);
    setAllChannels(Array.isArray(chans) ? chans : []);
  }, [id]);

  const loadViews = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/saved-filters/contacts?client_id=${parseInt(id)}`, { headers: await authHeaders() });
      if (res.ok) setSeqViews(await res.json());
    } catch (_) {}
  }, [id, authHeaders]);

  function saveView() {
    if (!seqTree || !Array.isArray(seqTree.and) || !seqTree.and.length) { toast.error('Apply a filter first'); return; }
    setViewName(''); setSaveModal(true);
  }
  async function doSaveView() {
    if (!viewName.trim()) return;
    setSavingView(true);
    try {
      const res = await fetch(`${API_BASE}/saved-filters/contacts`, {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({ client_id: parseInt(id), name: viewName.trim(), tree: seqTree, shared: false }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Save failed');
      const created = await res.json();
      await loadViews();
      setActiveViewId(created.id);
      setSaveModal(false);
      toast.success('View saved');
    } catch (e) { toast.error(e.message); }
    finally { setSavingView(false); }
  }

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    function onGenerated() { refreshSeqs(); }
    window.addEventListener('ce:sequenceGenerated', onGenerated);
    return () => window.removeEventListener('ce:sequenceGenerated', onGenerated);
  }, [refreshSeqs]);

  const seqActive = !!(seqTree && Array.isArray(seqTree.and) && seqTree.and.length > 0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!seqTree || !Array.isArray(seqTree.and) || seqTree.and.length === 0) { setSeqIdSet(null); return; }
      setSeqFilterLoading(true);
      try {
        const res = await fetch(`${API_BASE}/bulk/resolve-ids`, {
          method: 'POST', headers: await authHeaders(),
          body: JSON.stringify({ entity: 'contacts', client_id: parseInt(id), filter: seqTree }),
        });
        const data = await res.json();
        if (!cancelled && res.ok) setSeqIdSet(new Set((data.ids || []).map(Number)));
        else if (!cancelled) setSeqIdSet(new Set());
      } catch (e) { if (!cancelled) setSeqIdSet(new Set()); }
      finally { if (!cancelled) setSeqFilterLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [seqTree, id, authHeaders]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/filter/contacts/targets/campaign?client_id=${parseInt(id)}`, { headers: await authHeaders() });
        if (res.ok) setSeqTargets({ campaign: await res.json() });
      } catch (_) {}
    })();
    loadViews();
  }, [id, authHeaders, loadViews]);

  const seqByContact = useMemo(() => {
    const m = new Map();
    for (const s of allSequences) if (!m.has(s.contact_id)) m.set(s.contact_id, s);
    return m;
  }, [allSequences]);
  const chanByKey = useMemo(() => {
    const m = new Map();
    for (const c of allChannels) { const k = c.contact_id + ':' + c.channel; if (!m.has(k)) m.set(k, c); }
    return m;
  }, [allChannels]);
  const getSeq = useCallback((cid) => seqByContact.get(cid) || null, [seqByContact]);
  const getChannel = useCallback((cid, ch) => chanByKey.get(cid + ':' + ch) || null, [chanByKey]);

  const q = search.trim().toLowerCase();
  // When a contact counts as "new": when it was added, or when its newest sequence was written.
  const timeOf = useCallback((c) => {
    if (qv.basis === 'written') { const s = getSeq(c.id); return s && s.created_at ? new Date(s.created_at).getTime() : null; }
    return c.created_at ? new Date(c.created_at).getTime() : null;
  }, [qv.basis, getSeq]);
  const qStatusOf = useCallback((c) => {
    if (c.fit_status === 'outside_icp' || c.fit_status === 'not_a_fit') return 'outside';
    const s = getSeq(c.id);
    return !s ? 'none' : (s.approval_status === 'approved' ? 'approved' : 'pending');
  }, [getSeq]);
  // Search + Filters rail + time window. Status is applied after, so the chips can show counts.
  const baseFiltered = useMemo(() => {
    const [from, to] = windowFor(qv);
    return allContacts.filter(c => {
      if (q && !(c.full_name?.toLowerCase().includes(q) || c.job_title?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) || c.company_name?.toLowerCase().includes(q))) return false;
      if (seqActive && !(seqIdSet && seqIdSet.has(c.id))) return false;
      if (from != null || to != null) {
        const t = timeOf(c);
        if (t == null) return false;
        if (from != null && t < from) return false;
        if (to != null && t > to) return false;
      }
      return true;
    });
  }, [allContacts, q, seqActive, seqIdSet, qv, timeOf]);
  const qCounts = useMemo(() => {
    const n = { all: baseFiltered.length, none: 0, pending: 0, approved: 0, outside: 0 };
    for (const c of baseFiltered) n[qStatusOf(c)]++;
    return n;
  }, [baseFiltered, qStatusOf]);
  const filtered = useMemo(() => {
    const list = qv.status === 'all' ? baseFiltered.slice() : baseFiltered.filter(c => qStatusOf(c) === qv.status);
    if (qv.sort === 'name') list.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
    else {
      const dir = qv.sort === 'oldest' ? 1 : -1;
      list.sort((a, b) => ((timeOf(a) ?? 0) - (timeOf(b) ?? 0)) * dir);
    }
    return list;
  }, [baseFiltered, qv.status, qv.sort, qStatusOf, timeOf]);
  const qvActive = qv.status !== 'all' || qv.when !== 'any';

  const hasSelection = selectedIds.size > 0;
  const selectedContacts = useMemo(() => allContacts.filter(c => selectedIds.has(c.id)), [allContacts, selectedIds]);
  const statusOf = useCallback((cid) => {
    const s = getSeq(cid);
    return !s ? 'none' : (s.approval_status === 'approved' ? 'approved' : 'pending');
  }, [getSeq]);
  const selCounts = useMemo(() => {
    const n = { none: 0, pending: 0, approved: 0 };
    for (const c of selectedContacts) n[statusOf(c.id)]++;
    return n;
  }, [selectedContacts, statusOf]);
  const isOutside = (c) => c.fit_status === 'outside_icp' || c.fit_status === 'not_a_fit';
  const selNoFit = selectedContacts.filter(isOutside).length;
  const actionable = selectedContacts.filter(c => c.fit_status !== 'not_a_fit');
  const inCore = selectedContacts.filter(c => !isOutside(c));
  // Push to campaign: an outside-core-ICP contact you have already approved has been reviewed,
  // so it goes in. One still pending review is held, as is anyone marked not a fit.
  const pushable = selectedContacts.filter(c => {
    const s = getSeq(c.id);
    if (!s || c.fit_status === 'not_a_fit') return false;
    if (c.fit_status === 'outside_icp') return s.approval_status === 'approved';
    return true;
  });
  const pushHeldOutside = selectedContacts.filter(c => c.fit_status === 'outside_icp' && getSeq(c.id) && getSeq(c.id).approval_status !== 'approved').length;
  const pushNoSeq = selectedContacts.filter(c => !getSeq(c.id) && c.fit_status !== 'not_a_fit').length;
  const pushNoFit = selectedContacts.filter(c => c.fit_status === 'not_a_fit').length;
  const viewList = useMemo(() => {
    if (!hasSelection || statusFilter === 'everyone') return filtered;
    const sel = filtered.filter(c => selectedIds.has(c.id));
    if (statusFilter === 'selected') return sel;
    if (statusFilter === 'notfit') return sel.filter(c => c.fit_status === 'outside_icp' || c.fit_status === 'not_a_fit');
    return sel.filter(c => statusOf(c.id) === statusFilter);
  }, [filtered, hasSelection, statusFilter, selectedIds, statusOf]);
  useEffect(() => { if (!hasSelection && statusFilter !== 'everyone') setStatusFilter('everyone'); }, [hasSelection, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(viewList.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const paged = viewList.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const needSequence = selectedContacts.filter(c => !getSeq(c.id));
  const pendingSelected = inCore.filter(c => { const s = getSeq(c.id); return s && s.approval_status === 'pending'; });
  const pendingOutside = selectedContacts.filter(c => c.fit_status === 'outside_icp' && (() => { const s = getSeq(c.id); return s && s.approval_status === 'pending'; })());
  const approvedSelected = actionable.filter(c => { const s = getSeq(c.id); return s && s.approval_status === 'approved'; });
  const linkedinApproved = actionable.filter(c => { const ch = getChannel(c.id, 'linkedin'); return ch && ch.approval_status === 'approved'; });
  const emailApproved = actionable.filter(c => { const ch = getChannel(c.id, 'email'); return ch && ch.approval_status === 'approved'; });

  const navList = useMemo(() => (statusFilter !== 'everyone' ? viewList : (hasSelection ? filtered.filter(c => selectedIds.has(c.id)) : filtered)), [filtered, selectedIds, hasSelection, statusFilter, viewList]);

  const toggleSelect = (e, contactId) => {
    e.stopPropagation();
    setSelectedIds(prev => { const n = new Set(prev); if (n.has(contactId)) n.delete(contactId); else n.add(contactId); return n; });
  };
  const selectPage = () => setSelectedIds(new Set(paged.map(c => c.id)));
  const selectAll = () => setSelectedIds(new Set(filtered.map(c => c.id)));
  const clearAll = () => setSelectedIds(new Set());

  function goTo(contact) {
    if (!contact) return;
    setActiveContactId(contact.id);
    const idx = viewList.findIndex(c => c.id === contact.id);
    if (idx >= 0) setPage(Math.floor(idx / pageSize));
  }
  function move(delta) {
    if (!navList.length) return;
    let i = navList.findIndex(c => c.id === activeContactId);
    if (i < 0) i = delta > 0 ? -1 : navList.length;
    const next = i + delta;
    if (next < 0 || next >= navList.length) return;
    goTo(navList[next]);
  }

  const moveRef = useRef(() => {});
  moveRef.current = move;
  const modalOpenRef = useRef(false);
  modalOpenRef.current = batchOpen || !!tokenModal || campaignOpen || saveModal || confirmGenerateOpen;

  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (modalOpenRef.current) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (t && t.isContentEditable) return;
      if (tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (tag === 'INPUT' && t.type !== 'checkbox') return;
      e.preventDefault();
      moveRef.current(e.key === 'ArrowDown' ? 1 : -1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!tokenMenu) return;
    function onDown(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setTokenMenu(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [tokenMenu]);

  useEffect(() => {
    if (!viewList.length) { if (activeContactId !== null) setActiveContactId(null); return; }
    if (activeContactId === null || !viewList.some(c => c.id === activeContactId)) setActiveContactId(viewList[0].id);
  }, [viewList, activeContactId]);

  useEffect(() => {
    if (rbodyRef.current) rbodyRef.current.scrollTop = 0;
    setEditingStep(prev => (prev && prev.contactId !== activeContactId ? null : prev));
  }, [activeContactId]);

  useEffect(() => {
    if (!listRef.current || activeContactId == null) return;
    const el = listRef.current.querySelector(`[data-cid="${activeContactId}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeContactId, safePage]);

  async function approveChannel(channelId) {
    setApprovingChannel(channelId);
    try {
      const res = await fetch(`${API_BASE}/sequence-channels/${channelId}/approve`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ allow_outside_icp: true }) });
      if (!res.ok) throw new Error('Approve failed');
      await refreshSeqs();
      toast.success('Channel approved');
    } catch (e) { toast.error(e.message); }
    finally { setApprovingChannel(null); }
  }

  async function approveSequence(seqId) {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/sequences/${seqId}/approve`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ allow_outside_icp: true }) });
      if (!res.ok) throw new Error();
      await refreshSeqs();
      toast.success('Sequence approved');
    } catch (e) { toast.error('Approve failed'); }
  }

  async function regenerateOne(contact, ignoreFit) {
    const t = toast.loading('Generating sequence\u2026');
    try {
      const res = await fetch(`${API_BASE}/contacts/${contact.id}/generate-sequence`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: id, ignore_fit: !!ignoreFit }),
      });
      if (res.status === 402) { toast.error('Out of credits.', { id: t }); return; }
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data && data.not_a_fit) { toast(`Not a fit: ${data.reason}`, { id: t }); await fetchAll(); return; }
      const save = await fetch(`${API_BASE}/contacts/${contact.id}/sequences`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: id, email_sequence: data.email_sequence, linkedin_sequence: data.linkedin_sequence, call_sequence: data.call_sequence }),
      });
      if (!save.ok) throw new Error();
      await refreshSeqs();
      fetchAll();
      toast.success('Sequence generated', { id: t });
    } catch (e) { toast.error('Generate failed', { id: t }); }
  }

  function handleGenerate() {
    const toGen = needSequence.length > 0 ? needSequence : actionable;
    if (!toGen.length) return;
    setPendingGenerateCount(toGen.length);
    setConfirmGenerateOpen(true);
  }

  useEffect(() => {
    let timer = null;
    let cancelled = false;
    async function tick() {
      try {
        const res = await fetch(`${API_BASE}/clients/${id}/sequence-jobs/latest`, { headers: await authHeaders() });
        if (res.ok) {
          const j = await res.json();
          if (!cancelled && j && j.id) {
            if (LIVE_JOB.includes(j.status)) {
              activeJobRef.current = j.id;
              setGenerating(true);
              setProgress(progressFromJob(j));
              pollTickRef.current += 1;
              if (pollTickRef.current % 4 === 0) refreshSeqs();
            } else if (activeJobRef.current === j.id) {
              activeJobRef.current = null;
              setGenerating(false);
              setProgress(progressFromJob(j));
              refreshSeqs();
              fetchAll();
              const msg = {
                done: `Generated ${j.done} of ${j.total}${j.failed ? `, ${j.failed} failed` : ''}${j.not_a_fit ? `, ${j.not_a_fit} not a fit` : ''}${j.outside_icp ? `, ${j.outside_icp} outside core ICP (held for review)` : ''}`,
                stopped: `Stopped after ${j.done} of ${j.total}`,
                credits_out: `Out of credits after ${j.done} of ${j.total}`,
                failed: `Generation run failed: ${j.error || 'unknown error'}`,
              }[j.status];
              if (j.status === 'done' && !j.failed) toast.success(msg); else toast(msg);
              setTimeout(() => setProgress(null), 8000);
            }
          }
        }
      } catch (_) {}
      if (!cancelled) timer = setTimeout(tick, activeJobRef.current ? 3000 : 15000);
    }
    pollKickRef.current = () => { if (timer) clearTimeout(timer); tick(); };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); pollKickRef.current = null; };
  }, [id, authHeaders, refreshSeqs, fetchAll]);

  async function doGenerate() {
    setConfirmGenerateOpen(false);
    const toGen = needSequence.length > 0 ? needSequence : actionable;
    if (!toGen.length) return;
    try {
      const res = await fetch(`${API_BASE}/clients/${id}/sequence-jobs`, {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({ contact_ids: toGen.map(c => c.id), prompt }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) { toast.error('A generation run is already in progress for this client'); if (pollKickRef.current) pollKickRef.current(); return; }
      if (!res.ok) throw new Error(data.error || 'Could not start generation');
      activeJobRef.current = data.id;
      setGenerating(true);
      setProgress(progressFromJob(data));
      toast.success(`Generating ${data.total} sequence${data.total === 1 ? '' : 's'}`);
      if (pollKickRef.current) pollKickRef.current();
    } catch (e) { toast.error(e.message); }
  }

  async function stopGeneration() {
    const jid = activeJobRef.current;
    if (!jid) return;
    try {
      await fetch(`${API_BASE}/sequence-jobs/${jid}/stop`, { method: 'POST', headers: await authHeaders() });
      toast('Stopping after the sequences already in progress\u2026');
    } catch (_) {}
    if (pollKickRef.current) pollKickRef.current();
  }

  async function bulkApprove(outside) {
    const seqs = (outside ? pendingOutside : pendingSelected).map(c => getSeq(c.id)).filter(Boolean);
    if (!seqs.length) return;
    setApproving(true);
    const total = seqs.length;
    const tally = { ok: 0, failed: 0 };
    setProgress({ current: 0, total, status: `Approving 0 of ${total}` });
    const t = toast.loading(`Approving ${total}\u2026`);
    for (const group of chunk(seqs, 10)) {
      const token = await getToken();
      await Promise.all(group.map(async s => {
        try {
          const res = await fetch(`${API_BASE}/sequences/${s.id}/approve`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(outside ? { allow_outside_icp: true } : {}) });
          if (res.ok) tally.ok++; else tally.failed++;
        } catch (e) { tally.failed++; }
      }));
      setProgress({ current: tally.ok + tally.failed, total, status: `Approving ${tally.ok + tally.failed} of ${total}` });
    }
    await refreshSeqs();
    setApproving(false);
    if (tally.failed === 0) toast.success(`Approved ${tally.ok}`, { id: t });
    else toast.error(`Approved ${tally.ok}, ${tally.failed} failed`, { id: t });
    setProgress({ current: total, total, status: 'Complete.' });
    setTimeout(() => setProgress(null), 4000);
  }

  function startEditStep(contactId, channel, idx, currentBody) { setEditingStep({ contactId, channel, idx, body: currentBody || '' }); }
  function cancelEditStep() { setEditingStep(null); }

  async function insertTokenAtCursor(token) {
    const ta = textareaRef.current;
    if (!ta || !editingStep) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    let phrase = null;
    const t = toast.loading('Writing a sentence for the link\u2026');
    try {
      const res = await fetch(`${API_BASE}/token-place`, {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({ token, contact_id: editingStep.contactId, text: editingStep.body, channel: editingStep.channel }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data && data.sentence) phrase = data.sentence;
    } catch (e) {}
    if (!phrase) { toast.error('Could not write a natural sentence for that link. Nothing was added.', { id: t }); return; }
    toast.dismiss(t);
    const before0 = editingStep.body.slice(0, start);
    if (before0 && !/\s$/.test(before0)) phrase = ' ' + phrase;
    const before = before0, after = editingStep.body.slice(end);
    setEditingStep(prev => ({ ...prev, body: before + phrase + after }));
    setTimeout(() => { ta.focus(); const pos = start + phrase.length; ta.setSelectionRange(pos, pos); }, 0);
  }

  async function saveEditStep() {
    if (!editingStep) return;
    setSavingStep(true);
    try {
      const seq = getSeq(editingStep.contactId);
      if (!seq) throw new Error('Sequence not found');
      const emailSeq = parseSteps(seq, 'email');
      const linkedinSeq = parseSteps(seq, 'linkedin');
      const targetSeq = editingStep.channel === 'email' ? emailSeq : linkedinSeq;
      const updated = [...targetSeq];
      const bodyKey = editingStep.channel === 'email' ? 'body' : 'message';
      updated[editingStep.idx] = { ...updated[editingStep.idx], [bodyKey]: editingStep.body };
      const res = await fetch(`${API_BASE}/sequences/${seq.id}`, {
        method: 'PATCH', headers: await authHeaders(),
        body: JSON.stringify({
          email_sequence: editingStep.channel === 'email' ? updated : emailSeq,
          linkedin_sequence: editingStep.channel === 'linkedin' ? updated : linkedinSeq,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Step saved');
      setEditingStep(null);
      await refreshSeqs();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSavingStep(false);
  }

  async function loadAvailableBatches(channel) {
    try {
      const ep = channel === 'email' ? 'email-batches' : 'linkedin-batches';
      const res = await fetch(`${API_BASE}/clients/${id}/${ep}`);
      if (!res.ok) { setAvailableBatches([]); return; }
      const data = await res.json();
      setAvailableBatches(Array.isArray(data) ? data.filter(b => b.status !== 'exported') : []);
    } catch (e) { setAvailableBatches([]); }
  }
  function openBatch() {
    if (linkedinApproved.length + emailApproved.length === 0) { toast.error('Approve sequences first, then add them to a batch'); return; }
    const ch = linkedinApproved.length >= emailApproved.length ? 'linkedin' : 'email';
    setBatchChannel(ch); setCreatingNewBatch(false); setSelectedBatchId(''); setNewBatchName('');
    setBatchOpen(true);
    loadAvailableBatches(ch);
  }
  function changeBatchChannel(ch) { setBatchChannel(ch); setSelectedBatchId(''); loadAvailableBatches(ch); }
  async function submitBatch() {
    setBatchSubmitting(true);
    try {
      const channel = batchChannel;
      const eligible = channel === 'linkedin' ? linkedinApproved : emailApproved;
      const skipped = selectedContacts.length - eligible.length;
      if (eligible.length === 0) throw new Error(`No eligible contacts. ${skipped} skipped, they need an approved ${channel} sequence.`);
      const ep = channel === 'email' ? 'email-batches' : 'linkedin-batches';
      let batchId = selectedBatchId;
      if (creatingNewBatch) {
        if (!newBatchName.trim()) throw new Error('Batch name required');
        const body = channel === 'email' ? { name: newBatchName.trim(), destination_type: newBatchDestination } : { name: newBatchName.trim() };
        const cr = await fetch(`${API_BASE}/clients/${id}/${ep}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (!cr.ok) throw new Error('Could not create batch');
        batchId = (await cr.json()).id;
      }
      if (!batchId) throw new Error('Pick a batch or create a new one');
      const tally = { added: 0, failed: 0 };
      for (const contact of eligible) {
        const ch = getChannel(contact.id, channel);
        if (!ch) { tally.failed++; continue; }
        try {
          const res = await fetch(`${API_BASE}/${ep}/${batchId}/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ contact_id: contact.id, sequence_id: ch.sequence_id }) });
          if (res.ok) tally.added++; else tally.failed++;
        } catch (e) { tally.failed++; }
      }
      const parts = [`Added ${tally.added}`];
      if (tally.failed > 0) parts.push(`${tally.failed} failed`);
      if (skipped > 0) parts.push(`${skipped} skipped (no approved ${channel})`);
      toast.success(parts.join(' \u00B7 '));
      setBatchOpen(false);
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setBatchSubmitting(false); }
  }

  function openTokens(kind) {
    setTokenMenu(false);
    setTokenModal(kind);
    setTokenAction('insert');
    setTokenRows(freshTokenRows(kind === 'email' ? 'end_sales' : 'end_sales_li'));
    setTokenResult(null);
  }
  async function runTokens() {
    if (!selectedIds.size || !tokenModal) return;
    const enabled = Object.entries(tokenRows).filter(([, v]) => v.enabled);
    if (!enabled.length) { toast.error('Pick at least one token'); return; }
    const isLi = tokenModal === 'linkedin';
    setTokenRunning(true); setTokenResult(null);
    const perToken = [];
    try {
      const ids = actionable.map(c => c.id);
      for (const [tk, cfg] of enabled) {
        const endpoint = tokenAction === 'insert'
          ? (isLi ? '/sequences/bulk-insert-linkedin-token' : '/sequences/bulk-insert-token')
          : (isLi ? '/sequences/bulk-remove-linkedin-token' : '/sequences/bulk-remove-token');
        const agg = { token: tk, modified: 0, placed: 0, skipped: 0 };
        for (let i = 0; i < ids.length; i += 25) {
          const chunkIds = ids.slice(i, i + 25);
          setTokenProgress(`${Math.min(i + 25, ids.length)} of ${ids.length}`);
          const authTok = await getToken();
          const body = tokenAction === 'insert'
            ? { contact_ids: chunkIds, token: tk, strategy: cfg.strategy }
            : { contact_ids: chunkIds, token: tk };
          const res = await fetch(API_BASE + endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authTok}` }, body: JSON.stringify(body) });
          if (!res.ok) throw new Error(tk + ' failed: ' + await res.text());
          const d = await res.json();
          agg.modified += d.modified || 0; agg.placed += d.placed || 0; agg.skipped += d.skipped || 0;
        }
        perToken.push(agg);
      }
      setTokenResult({ perToken, requested: selectedIds.size });
      const totalMods = perToken.reduce((a, r) => a + (r.modified || 0), 0);
      toast.success(`${tokenAction === 'insert' ? 'Inserted' : 'Removed'} ${enabled.length} token type${enabled.length === 1 ? '' : 's'}, ${totalMods} updates`);
      await refreshSeqs();
    } catch (e) { toast.error('Token update failed: ' + e.message); }
    setTokenProgress('');
    setTokenRunning(false);
  }

  async function pushToCampaign() {
    if (!ceCampaign) { toast.error('Pick a campaign'); return; }
    const outsideIds = new Set(pushable.filter(c => c.fit_status === 'outside_icp').map(c => c.id));
    const seqs = pushable.map(c => getSeq(c.id)).filter(Boolean);
    if (!seqs.length) { toast.error('Nothing in the selection can be enrolled'); return; }
    setPushingToCampaign(true);
    const total = seqs.length;
    const tally = { ok: 0, failed: 0 };
    setProgress({ current: 0, total, status: `Enrolling 0 of ${total}` });
    const t = toast.loading(`Enrolling ${total} into campaign\u2026`);
    for (const group of chunk(seqs, 5)) {
      const token = await getToken();
      await Promise.all(group.map(async s => {
        try {
          const res = await fetch(`${API_BASE}/sequences/${s.id}/approve`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ campaign_id: parseInt(ceCampaign), ...(outsideIds.has(s.contact_id) ? { allow_outside_icp: true } : {}) }) });
          const data = res.ok ? await res.json().catch(() => ({})) : null;
          if (data && data.enrolment && (data.enrolment.status === 'enrolled' || data.enrolment.status === 'already_finished')) tally.ok++; else tally.failed++;
        } catch (e) { tally.failed++; }
      }));
      setProgress({ current: tally.ok + tally.failed, total, status: `Enrolling ${tally.ok + tally.failed} of ${total}` });
    }
    await refreshSeqs();
    setPushingToCampaign(false);
    setCampaignOpen(false);
    if (tally.failed === 0) toast.success(`Enrolled ${tally.ok} into campaign`, { id: t });
    else toast.error(`Enrolled ${tally.ok}, ${tally.failed} failed`, { id: t });
    setProgress({ current: total, total, status: 'Complete.' });
    setTimeout(() => setProgress(null), 4000);
  }

  useEffect(() => {
    rapContactRef.current = activeContactId;
    setRapThread([]);
    setRapText('');
  }, [activeContactId]);

  useEffect(() => {
    try { localStorage.setItem('sonar.seqRaptorOpen', rapOpen ? '1' : '0'); } catch (_) {}
  }, [rapOpen]);

  useEffect(() => {
    if (rapThreadRef.current) rapThreadRef.current.scrollTop = rapThreadRef.current.scrollHeight;
  }, [rapThread, rapAsking]);

  async function askRaptor() {
    const q = rapText.trim();
    if (!q || rapAsking || !activeContact || !activeSeq) return;
    const history = rapThread.slice(-6);
    const forContact = activeContact.id;
    setRapText('');
    setRapThread(t => [...t, { role: 'user', text: q }]);
    setRapAsking(true);
    try {
      const r = await fetch(`${API_BASE}/clients/${id}/sequences/ask`, {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({ contact_id: activeContact.id, sequence_id: activeSeq.id, question: q, history }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Raptor could not answer');
      if (rapContactRef.current === forContact) setRapThread(t => [...t, { role: 'raptor', text: (j.answer || '') + (j.note ? `\n\n_${j.note}_` : ''), proposals: Array.isArray(j.proposals) ? j.proposals : [], seqId: j.sequence_id, applied: {}, dismissed: false }]);
    } catch (e) {
      if (rapContactRef.current === forContact) setRapThread(t => [...t, { role: 'raptor', text: `Could not answer: ${e.message}` }]);
    } finally {
      setRapAsking(false);
    }
  }

  async function applyRaptorEdits(mi, idxs) {
    const msg = rapThread[mi];
    if (!msg || !idxs.length) return;
    const seq = allSequences.find(s => s.id === msg.seqId);
    if (!seq) { toast.error('That sequence has been replaced since Raptor read it. Ask again.'); return; }
    const nextE = [...parseSteps(seq, 'email')];
    const nextL = [...parseSteps(seq, 'linkedin')];
    for (const k of idxs) {
      const p = msg.proposals[k];
      const arr = p.channel === 'email' ? nextE : nextL;
      const pos = arr.findIndex((s, i) => Number(s.step || i + 1) === Number(p.step));
      if (pos < 0) { toast.error(`${p.channel === 'email' ? 'Email' : 'LinkedIn'} step ${p.step} no longer exists`); return; }
      const key = p.channel === 'email' ? 'body' : 'message';
      arr[pos] = { ...arr[pos], [key]: p.text };
    }
    try {
      const res = await fetch(`${API_BASE}/sequences/${seq.id}`, {
        method: 'PATCH', headers: await authHeaders(),
        body: JSON.stringify({ email_sequence: nextE, linkedin_sequence: nextL }),
      });
      if (!res.ok) throw new Error(await res.text());
      setRapThread(t => t.map((m, i) => (i === mi ? { ...m, applied: { ...(m.applied || {}), ...Object.fromEntries(idxs.map(k => [k, true])) } } : m)));
      toast.success(`Replaced ${idxs.length} step${idxs.length === 1 ? '' : 's'}`);
      await refreshSeqs();
    } catch (e) { toast.error('Replace failed: ' + e.message); }
  }

  function channelPills(cid) {
    const em = getChannel(cid, 'email');
    const li = getChannel(cid, 'linkedin');
    if (!em && !li) return <span className="crmsq3-pill crmsq3-pill--none">No sequence</span>;
    return (<>
      {em && <span className={'crmsq3-pill crmsq3-pill--' + (em.approval_status === 'approved' ? 'approved' : 'pending')}>Em {em.approval_status}</span>}
      {li && <span className={'crmsq3-pill crmsq3-pill--' + (li.approval_status === 'approved' ? 'approved' : 'pending')}>Li {li.approval_status}</span>}
    </>);
  }

  const activeContact = activeContactId != null ? (allContacts.find(c => c.id === activeContactId) || null) : null;
  const activeSeq = activeContact ? getSeq(activeContact.id) : null;
  const activeEmailSteps = parseSteps(activeSeq, 'email');
  const activeLiSteps = parseSteps(activeSeq, 'linkedin');
  const activeIdx = navList.findIndex(c => c.id === activeContactId);
  const navLabel = !navList.length ? '' : activeIdx < 0
    ? (hasSelection ? 'Not in selection' : '')
    : `${activeIdx + 1} of ${navList.length}${hasSelection ? ' selected' : ''}`;

  function renderSteps(contact, steps, channel, ch) {
    return (
      <div className="crmsq3-ch">
        <div className="crmsq3-ch-h">
          <span className={'crmsq3-ch-t crmsq3-ch-t--' + (channel === 'email' ? 'email' : 'li')}>
            {channel === 'email' ? 'Email' : 'LinkedIn'}
            {ch && <span className={'crmsq3-pill crmsq3-pill--' + (ch.approval_status === 'approved' ? 'approved' : 'pending')}>{ch.approval_status}</span>}
          </span>
          {ch && ch.approval_status !== 'approved' && contact.fit_status !== 'not_a_fit' && (
            <button className="crmsq3-btn crmsq3-btn--ghost crmsq3-btn--sm" disabled={approvingChannel === ch.id} onClick={() => approveChannel(ch.id)}>
              {approvingChannel === ch.id ? 'Approving\u2026' : (channel === 'email' ? 'Approve email' : 'Approve LinkedIn')}
            </button>
          )}
        </div>
        {steps.map((step, i) => {
          const isEditing = editingStep && editingStep.contactId === contact.id && editingStep.channel === channel && editingStep.idx === i;
          const bodyText = channel === 'email' ? step.body : step.message;
          return (
            <div key={i} className="crmsq3-step">
              <div className="crmsq3-step-h">
                <span>Step {step.step || i + 1}{channel === 'linkedin' && step.type ? ' \u00B7 ' + step.type.replace(/_/g, ' ') : ''}</span>
                {!isEditing && <button className="crmsq3-link" onClick={() => startEditStep(contact.id, channel, i, bodyText)}>Edit</button>}
              </div>
              {channel === 'email' && step.subject && <div className="crmsq3-step-subj">{step.subject}</div>}
              {isEditing ? (
                <div>
                  {channel === 'email' ? (
                    <div className="crmsq3-tokbtns">
                      <button type="button" className="crmsq3-tokbtn" onClick={() => insertTokenAtCursor('cta_url')}>+ {'{{cta_url}}'}</button>
                      <button type="button" className="crmsq3-tokbtn" onClick={() => insertTokenAtCursor('landing_page')}>+ {'{{landing_page}}'}</button>
                      <button type="button" className="crmsq3-tokbtn" onClick={() => insertTokenAtCursor('attachment')}>+ {'{{attachment}}'}</button>
                    </div>
                  ) : <div className="crmsq3-tokhint">Use LinkedIn tokens in the bulk bar for LinkedIn links.</div>}
                  <textarea ref={textareaRef} className="crmsq3-step-ta" value={editingStep.body} onChange={e => setEditingStep(prev => ({ ...prev, body: e.target.value }))} />
                  <div className="crmsq3-editbtns">
                    <button className="crmsq3-btn crmsq3-btn--sm" onClick={saveEditStep} disabled={savingStep}>{savingStep ? 'Saving\u2026' : 'Save'}</button>
                    <button className="crmsq3-btn crmsq3-btn--ghost crmsq3-btn--sm" onClick={cancelEditStep} disabled={savingStep}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="crmsq3-step-body">{bodyText}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="crmsq3">
      <div className="crmsq3-head">
        <div>
          <h1 className="crmsq3-title">Sequences</h1>
          <p className="crmsq3-sub">{allContacts.length} contacts {'\u00B7'} {allSequences.length} sequences</p>
        </div>
        <div className="crmsq3-count"><b>{filtered.length}</b>{seqActive || q ? ' filtered' : ` of ${allContacts.length}`}</div>
      </div>

      {error && <div className="crmsq3-empty">{error}</div>}

      <div className="crmsq3-tabs">
        <button className={'crmsq3-tab' + (activeViewId === null ? ' crmsq3-tab--on' : '')} onClick={() => { setActiveViewId(null); setSeqTree(null); setPage(0); }}>All sequences</button>
        {seqViews.map(v => (
          <button key={v.id} className={'crmsq3-tab' + (activeViewId === v.id ? ' crmsq3-tab--on' : '')} onClick={() => { setActiveViewId(v.id); setSeqTree(v.tree); setPage(0); }}>{v.name}</button>
        ))}
        <button className="crmsq3-tab" title="New view" onClick={() => setShowFilters(true)}>+</button>
      </div>

      <div className="crmsq3-toolbar">
        <div className="crmsq3-search">
          <input placeholder="Search name, title, email, company" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
        </div>
        <button className={'crmsq3-tb-btn' + (showFilters ? ' crmsq3-tb-btn--on' : '')} onClick={() => setShowFilters(v => !v)}>
          Filters{seqActive ? ` \u00B7 ${seqTree.and.length}` : ''}
        </button>
        {seqActive && <button className="crmsq3-tb-btn" onClick={saveView}>Save view</button>}
        <div className="crmsq3-tb-right">
          <button className="crmsq3-link" onClick={selectPage}>Select page ({paged.length})</button>
          <button className="crmsq3-link" onClick={selectAll}>Select all {filtered.length}</button>
          {hasSelection && <button className="crmsq3-link" onClick={clearAll}>Clear ({selectedIds.size})</button>}
        </div>
      </div>

      <div className="crmsq3-qv">
        {[['all', 'All'], ['none', 'No sequence'], ['pending', 'Pending'], ['approved', 'Approved'], ['outside', 'Outside core ICP']].map(([k, label]) => (
          <button key={k} className={'crmsq3-chip' + (k === 'none' ? ' crmsq3-chip--none' : k === 'pending' ? ' crmsq3-chip--pending' : k === 'approved' ? ' crmsq3-chip--approved' : '') + (qv.status === k ? ' crmsq3-chip--on' : '')} onClick={() => setQ({ status: k })}>
            {label} {qCounts[k]}
          </button>
        ))}
        <span className="crmsq3-qv-sep" />
        <select className="crmsq3-qv-sel" value={qv.when} onChange={e => setQ({ when: e.target.value })} title="Time window">
          <option value="any">Any time</option>
          <option value="1h">Last hour</option>
          <option value="today">Today</option>
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="custom">Custom range</option>
        </select>
        {qv.when === 'custom' && (
          <>
            <input type="datetime-local" className="crmsq3-qv-dt" value={qv.from} onChange={e => setQ({ from: e.target.value })} title="From" />
            <span className="crmsq3-qv-to">to</span>
            <input type="datetime-local" className="crmsq3-qv-dt" value={qv.to} onChange={e => setQ({ to: e.target.value })} title="To (leave empty for now)" />
          </>
        )}
        <select className="crmsq3-qv-sel" value={qv.basis} onChange={e => setQ({ basis: e.target.value })} title="Which time to use">
          <option value="added">by contact added</option>
          <option value="written">by sequence written</option>
        </select>
        <select className="crmsq3-qv-sel" value={qv.sort} onChange={e => setQ({ sort: e.target.value })} title="Sort">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name A to Z</option>
        </select>
        {qvActive && <button className="crmsq3-link" onClick={() => setQ({ status: 'all', when: 'any', from: '', to: '' })}>Reset</button>}
      </div>

      {progress && (
        <div className="crmsq3-prog">
          <div className="crmsq3-prog-bar"><div className="crmsq3-prog-fill" style={{ width: (progress.total ? Math.round(progress.current / progress.total * 100) : 0) + '%' }} /></div>
          <span className="crmsq3-prog-txt">{progress.status}</span>
          {generating && <button className="crmsq3-btn crmsq3-btn--danger crmsq3-btn--sm" onClick={stopGeneration}>Stop</button>}
        </div>
      )}

      <div className="crmsq3-body">
        {showFilters && (
          <div className="crmsq3-rail">
            <FilterRail
              schema={SEQUENCE_SCHEMA}
              value={seqTree}
              onChange={(t) => { setSeqTree(t); setPage(0); }}
              matchCount={seqActive ? filtered.length : undefined}
              loading={seqFilterLoading}
              savedViews={seqViews}
              targets={seqTargets}
              onApplyView={(v) => { setSeqTree(v.tree); setPage(0); }}
            />
          </div>
        )}

        <div className={'crmsq3-panes' + (rapOpen ? ' crmsq3-panes--rap' : ' crmsq3-panes--rapmin')}>
          <div className="crmsq3-left">
            {hasSelection && (
              <div className="crmsq3-strip">
                <span className="crmsq3-strip-n">{selectedIds.size} selected</span>
                <button className={'crmsq3-chip' + (statusFilter === 'selected' ? ' crmsq3-chip--on' : '')} onClick={() => { setStatusFilter('selected'); setPage(0); }}>All {selectedIds.size}</button>
                {selCounts.none > 0 && <button className={'crmsq3-chip crmsq3-chip--none' + (statusFilter === 'none' ? ' crmsq3-chip--on' : '')} onClick={() => { setStatusFilter('none'); setPage(0); }}>{selCounts.none} no sequence</button>}
                {selCounts.pending > 0 && <button className={'crmsq3-chip crmsq3-chip--pending' + (statusFilter === 'pending' ? ' crmsq3-chip--on' : '')} onClick={() => { setStatusFilter('pending'); setPage(0); }}>{selCounts.pending} pending</button>}
                {selCounts.approved > 0 && <button className={'crmsq3-chip crmsq3-chip--approved' + (statusFilter === 'approved' ? ' crmsq3-chip--on' : '')} onClick={() => { setStatusFilter('approved'); setPage(0); }}>{selCounts.approved} approved</button>}
                {selNoFit > 0 && <button className={'crmsq3-chip crmsq3-chip--none' + (statusFilter === 'notfit' ? ' crmsq3-chip--on' : '')} onClick={() => { setStatusFilter('notfit'); setPage(0); }}>{selNoFit} outside core ICP</button>}
                {statusFilter !== 'everyone' && <button className="crmsq3-link crmsq3-strip-reset" onClick={() => { setStatusFilter('everyone'); setPage(0); }}>Show everyone</button>}
              </div>
            )}
            <div className="crmsq3-list" ref={listRef}>
              {paged.map((contact, i) => {
                const isSel = selectedIds.has(contact.id);
                const isActive = contact.id === activeContactId;
                return (
                  <div key={contact.id} data-cid={contact.id}
                       className={'crmsq3-row' + (isActive ? ' crmsq3-row--active' : '')}
                       onClick={() => setActiveContactId(contact.id)}>
                    <input type="checkbox" className="crmsq3-check" checked={isSel} onChange={e => toggleSelect(e, contact.id)} onClick={e => e.stopPropagation()} />
                    <span className="crmsq3-av" style={{ background: avatarColour(contact.full_name) }}>{initials(contact.full_name)}</span>
                    <div className="crmsq3-row-main">
                      <div className="crmsq3-row-name"><span className="crmsq3-row-n">#{safePage * pageSize + i + 1}</span>{contact.full_name}{timeOf(contact) && <span className="crmsq3-row-time" title={qv.basis === 'written' ? 'Sequence written' : 'Contact added'}>{shortWhen(timeOf(contact))}</span>}</div>
                      <div className="crmsq3-row-sub">{contact.job_title}{contact.company_name ? ' \u00B7 ' + contact.company_name : ''}</div>
                      <div className="crmsq3-pills">{(contact.fit_status === 'outside_icp' || contact.fit_status === 'not_a_fit') && <span className="crmsq3-pill crmsq3-pill--nofit" title={contact.fit_reason || ''}>Outside core ICP</span>}{channelPills(contact.id)}</div>
                    </div>
                  </div>
                );
              })}
              {viewList.length === 0 && <div className="crmsq3-empty">No contacts found.</div>}
            </div>
            <div className="crmsq3-pager">
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={250}>250 per page</option>
                <option value={500}>500 per page</option>
              </select>
              <span>{viewList.length === 0 ? '0' : `${safePage * pageSize + 1}\u2013${safePage * pageSize + paged.length}`} of {viewList.length}</span>
              <span>
                <button disabled={safePage <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</button>{' '}
                <button disabled={safePage >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
              </span>
            </div>
          </div>

          <div className="crmsq3-right">
            {!activeContact ? (
              <div className="crmsq3-empty">Pick a contact to read their sequence.</div>
            ) : (<>
              <div className="crmsq3-rhd">
                <span className="crmsq3-av crmsq3-av--lg" style={{ background: avatarColour(activeContact.full_name) }}>{initials(activeContact.full_name)}</span>
                <div className="crmsq3-rhd-id">
                  <div className="crmsq3-rhd-nm">{activeContact.full_name}</div>
                  <div className="crmsq3-rhd-rl">{[activeContact.job_title, activeContact.company_name, activeContact.email].filter(Boolean).join(' \u00B7 ')}</div>
                </div>
                <div className="crmsq3-nav">
                  <button aria-label="Previous contact" disabled={activeIdx <= 0} onClick={() => move(-1)}>{'\u2039'}</button>
                  <span>{navLabel}</span>
                  <button aria-label="Next contact" disabled={activeIdx < 0 || activeIdx >= navList.length - 1} onClick={() => move(1)}>{'\u203A'}</button>
                </div>
                <button className="crmsq3-btn crmsq3-btn--ghost crmsq3-btn--sm" onClick={() => navigate(`/clients/${id}/crm/contacts/${activeContact.id}`, { state: { from: `/clients/${id}/crm/sequences` } })}>Open record</button>
              </div>
              <div className="crmsq3-rbody" ref={rbodyRef}>
                {(activeContact.fit_status === 'outside_icp' || activeContact.fit_status === 'not_a_fit') && (
                  <div className="crmsq3-nofit">
                    <div className="crmsq3-nofit-h">Outside core ICP, held from bulk approval</div>
                    <div className="crmsq3-nofit-r">{activeContact.fit_reason || 'Outside the core ICP.'}{activeContact.fit_status === 'not_a_fit' ? ' This sequence was written before the bridging rules. Regenerate it before approving.' : ' Review the sequence and approve it here if you want to reach out.'}</div>
                  </div>
                )}
                {!activeSeq ? (
                  <div className="crmsq3-empty">No sequence yet for this contact.</div>
                ) : (<>
                  {activeEmailSteps.length > 0 && renderSteps(activeContact, activeEmailSteps, 'email', getChannel(activeContact.id, 'email'))}
                  {activeLiSteps.length > 0 && renderSteps(activeContact, activeLiSteps, 'linkedin', getChannel(activeContact.id, 'linkedin'))}
                  {activeEmailSteps.length === 0 && activeLiSteps.length === 0 && <div className="crmsq3-empty">Sequence has no steps.</div>}
                </>)}
              </div>
              <div className="crmsq3-rft">
                {activeSeq && activeContact.fit_status !== 'not_a_fit' && activeSeq.approval_status !== 'approved' && <button className="crmsq3-btn" onClick={() => approveSequence(activeSeq.id)}>{activeContact.fit_status === 'outside_icp' ? 'Approve anyway' : 'Approve sequence'}</button>}
                <span className="crmsq3-approved">{activeSeq && activeContact.fit_status !== 'not_a_fit' && activeSeq.approval_status === 'approved' ? '\u2713 Approved' : ''}</span>
                <button className="crmsq3-btn crmsq3-btn--ghost" onClick={() => regenerateOne(activeContact)}>{activeSeq ? 'Regenerate' : 'Generate'}</button>
              </div>
            </>)}
          </div>

          {rapOpen ? (
            <aside className="crmsq3-rap">
              <div className="crmsq3-rap-hd">
                <span className="crmh-panel-mark"><img src={raptorMark} alt="Raptor" /></span>
                <div>
                  <strong>Raptor</strong>
                  <span className="crmsq3-rap-sub">{activeContact ? `Reading ${activeContact.full_name}'s sequence` : 'Reads the sequence with you'}</span>
                </div>
                <button type="button" className="crmsq3-rap-x" onClick={() => setRapOpen(false)}>Hide</button>
              </div>
              <div className="crmsq3-rap-thread" ref={rapThreadRef}>
                {rapThread.length === 0 ? (
                  <div className="crmpf-raptor-intro">
                    <div className="crmh-intro-mark"><img src={raptorMark} alt="Raptor" /></div>
                    <p>{activeSeq ? 'Ask about this sequence, or ask Raptor to rewrite any steps, for example LinkedIn 1 to 3 or email 2 and 3. You choose which changes to keep.' : 'Pick a contact with a sequence and ask about it here.'}</p>
                  </div>
                ) : rapThread.map((m, i) => (
                  <div key={i} className={'crmpf-msg crmpf-msg--' + m.role}>
                    {m.role === 'raptor'
                      ? <div className="crmpf-md" data-color-mode="light"><MDEditor.Markdown source={m.text} /></div>
                      : <p>{m.text}</p>}
                    {m.role === 'raptor' && !m.dismissed && Array.isArray(m.proposals) && m.proposals.length > 0 && (() => {
                      const pending = m.proposals.map((_, k) => k).filter(k => !(m.applied && m.applied[k]));
                      return (
                        <div className="crmsq3-rap-card">
                          <div className="crmsq3-rap-card-h">{m.proposals.length === 1 ? `Proposed replacement \u00B7 ${m.proposals[0].channel === 'email' ? 'Email' : 'LinkedIn'} step ${m.proposals[0].step}` : `Proposed replacements \u00B7 ${m.proposals.length} steps`}</div>
                          {m.proposals.map((p, k) => (
                            <div key={k} className="crmsq3-rap-prop">
                              <div className="crmsq3-rap-prop-l">{p.channel === 'email' ? 'Email' : 'LinkedIn'} step {p.step}</div>
                              <div className="crmsq3-rap-cur">{p.current}</div>
                              <div className="crmsq3-rap-new">{p.text}</div>
                              {m.applied && m.applied[k]
                                ? <div className="crmsq3-rap-done">{'\u2713'} Replaced</div>
                                : <button type="button" className="crmsq3-btn crmsq3-btn--ghost crmsq3-btn--sm" onClick={() => applyRaptorEdits(i, [k])}>Replace this step</button>}
                            </div>
                          ))}
                          <div className="crmsq3-rap-card-f">
                            {pending.length > 1 && <button type="button" className="crmsq3-btn crmsq3-btn--sm" onClick={() => applyRaptorEdits(i, pending)}>Replace all {pending.length}</button>}
                            {pending.length > 0 && <button type="button" className="crmsq3-btn crmsq3-btn--ghost crmsq3-btn--sm" onClick={() => setRapThread(t => t.map((x, j) => (j === i ? { ...x, dismissed: true } : x)))}>Dismiss</button>}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
                {rapAsking && <div className="crmpf-msg crmpf-msg--raptor"><p className="crmpf-muted">Thinking</p></div>}
              </div>
              <div className="crmsq3-rap-comp">
                <div className="crmpf-composer">
                  <img className="crm-composer-raptor" src={raptorIcon} alt="Raptor" />
                  <textarea
                    value={rapText}
                    onChange={e => setRapText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askRaptor(); } }}
                    placeholder={activeSeq ? 'Ask about this sequence' : 'No sequence to ask about'}
                    rows={2}
                    disabled={!activeSeq}
                  />
                  <button type="button" onClick={askRaptor} disabled={rapAsking || !rapText.trim() || !activeSeq}>Ask</button>
                </div>
              </div>
            </aside>
          ) : (
            <button type="button" className="crmsq3-rap-min" onClick={() => setRapOpen(true)} title="Show Raptor">
              <span className="crmh-panel-mark"><img src={raptorMark} alt="Raptor" /></span>
              <span className="crmsq3-rap-min-l">Raptor</span>
            </button>
          )}
        </div>
      </div>

      {hasSelection && (
        <div className="crmsq3-bar">
          <span className="crmsq3-bar-count">{selectedIds.size} selected</span>
          <span className="crmsq3-bar-tags">
            {needSequence.length > 0 && <span>{needSequence.length} need sequences</span>}
            {pendingSelected.length > 0 && <span>{pendingSelected.length} pending</span>}
            {approvedSelected.length > 0 && <span>{approvedSelected.length} approved</span>}
            {selNoFit > 0 && <span>{selNoFit} outside core ICP, held for review</span>}
          </span>
          <div className="crmsq3-bar-right">
            <input className="crmsq3-bar-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Optional instructions" />
            <button className="crmsq3-btn" onClick={handleGenerate} disabled={generating || approving}>
              {generating ? 'Generating\u2026' : needSequence.length > 0 ? `Generate ${needSequence.length}` : `Regenerate ${actionable.length}`}
            </button>
            {pendingSelected.length > 0 && (
              <button className="crmsq3-btn crmsq3-btn--ghost" onClick={() => bulkApprove(false)} disabled={approving || generating}>
                {approving ? 'Approving\u2026' : `Approve ${pendingSelected.length}`}
              </button>
            )}
            {pendingOutside.length > 0 && (
              <button className="crmsq3-btn crmsq3-btn--ghost" onClick={() => bulkApprove(true)} disabled={approving || generating}>
                {`Approve ${pendingOutside.length} outside core ICP`}
              </button>
            )}
            <button className="crmsq3-btn crmsq3-btn--ghost" onClick={openBatch}>Add to batch</button>
            <div className="crmsq3-menu" ref={menuRef}>
              <button className="crmsq3-btn crmsq3-btn--ghost" onClick={() => setTokenMenu(v => !v)}>Link tokens {'\u25BE'}</button>
              {tokenMenu && (
                <div className="crmsq3-pop">
                  <button onClick={() => openTokens('email')}>Email tokens</button>
                  <button onClick={() => openTokens('linkedin')}>LinkedIn tokens</button>
                </div>
              )}
            </div>
            <button className="crmsq3-btn crmsq3-btn--ghost" onClick={() => { setCeCampaign(''); setCampaignOpen(true); }}>Push to campaign</button>
            <button className="crmsq3-btn crmsq3-btn--ghost" onClick={clearAll}>Clear</button>
          </div>
        </div>
      )}

      {saveModal && (
        <Modal title="Save view" onClose={() => setSaveModal(false)} busy={savingView}
          footer={<>
            <button className="crmsq3-btn crmsq3-btn--ghost" onClick={() => setSaveModal(false)} disabled={savingView}>Cancel</button>
            <button className="crmsq3-btn" onClick={doSaveView} disabled={savingView || !viewName.trim()}>{savingView ? 'Saving\u2026' : 'Save view'}</button>
          </>}>
          <span className="crmsq3-modal-lbl">View name</span>
          <input className="crmsq3-modal-inp" autoFocus value={viewName} onChange={e => setViewName(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter') doSaveView(); }} placeholder="Ready to approve" />
          <p className="crmsq3-modal-note">Saves the current filter as a tab you can come back to.</p>
        </Modal>
      )}

      {batchOpen && (
        <Modal title="Add to batch" onClose={() => setBatchOpen(false)} busy={batchSubmitting}
          footer={<>
            <button className="crmsq3-btn crmsq3-btn--ghost" onClick={() => setBatchOpen(false)} disabled={batchSubmitting}>Cancel</button>
            <button className="crmsq3-btn" onClick={submitBatch} disabled={batchSubmitting}>{batchSubmitting ? 'Adding\u2026' : 'Add'}</button>
          </>}>
          <p className="crmsq3-modal-s">{selectedIds.size} selected {'\u00B7'} {batchChannel === 'linkedin' ? linkedinApproved.length : emailApproved.length} eligible</p>
          <div className="crmsq3-modal-sec">
            <span className="crmsq3-modal-lbl">Channel</span>
            <div className="crmsq3-seg">
              <button className={batchChannel === 'linkedin' ? 'crmsq3-seg-on' : ''} onClick={() => changeBatchChannel('linkedin')}>LinkedIn ({linkedinApproved.length})</button>
              <button className={batchChannel === 'email' ? 'crmsq3-seg-on' : ''} onClick={() => changeBatchChannel('email')}>Email ({emailApproved.length})</button>
            </div>
          </div>
          <div className="crmsq3-modal-sec">
            <span className="crmsq3-modal-lbl">Target batch</span>
            {!creatingNewBatch ? (<>
              <select className="crmsq3-modal-sel" value={selectedBatchId} onChange={e => setSelectedBatchId(e.target.value)}>
                <option value="">Choose a batch</option>
                {availableBatches.map(b => <option key={b.id} value={b.id}>{b.name} ({b.contact_count} contacts)</option>)}
              </select>
              <p className="crmsq3-modal-note"><button className="crmsq3-link" onClick={() => setCreatingNewBatch(true)}>Create new batch</button></p>
            </>) : (<>
              <input className="crmsq3-modal-inp" value={newBatchName} onChange={e => setNewBatchName(e.target.value)} placeholder="New batch name" autoFocus />
              {batchChannel === 'email' && (
                <select className="crmsq3-modal-sel" value={newBatchDestination} onChange={e => setNewBatchDestination(e.target.value)} style={{ marginTop: 8 }}>
                  {EMAIL_DESTINATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              )}
              <p className="crmsq3-modal-note"><button className="crmsq3-link" onClick={() => setCreatingNewBatch(false)}>Pick existing</button></p>
            </>)}
          </div>
          <p className="crmsq3-modal-note">Only contacts with an approved {batchChannel} sequence are added. Others are skipped.</p>
        </Modal>
      )}

      {tokenModal && (
        <Modal title={tokenModal === 'email' ? 'Email link tokens' : 'LinkedIn link tokens'} onClose={() => setTokenModal(null)} busy={tokenRunning}
          footer={<>
            <button className="crmsq3-btn crmsq3-btn--ghost" onClick={() => setTokenModal(null)} disabled={tokenRunning}>Close</button>
            <button className="crmsq3-btn" onClick={runTokens} disabled={tokenRunning}>
              {tokenRunning ? (tokenProgress ? `Working\u2026 ${tokenProgress}` : 'Running\u2026') : `${tokenAction === 'insert' ? 'Insert' : 'Remove'} across ${selectedIds.size}`}
            </button>
          </>}>
          <p className="crmsq3-modal-s">
            {tokenModal === 'email'
              ? `${selectedIds.size} contacts selected. Tokens are swapped for the URLs set on the client, or a contact's own override, at send time.`
              : `${selectedIds.size} contacts selected. LinkedIn messages only. Email sequences are not touched.`}
          </p>
          <div className="crmsq3-modal-sec">
            <span className="crmsq3-modal-lbl">Action</span>
            <div className="crmsq3-seg">
              <button className={tokenAction === 'insert' ? 'crmsq3-seg-on' : ''} onClick={() => setTokenAction('insert')}>Insert</button>
              <button className={tokenAction === 'remove' ? 'crmsq3-seg-on' : ''} onClick={() => setTokenAction('remove')}>Remove</button>
            </div>
          </div>
          <div className="crmsq3-modal-sec">
            <span className="crmsq3-modal-lbl">Tokens and placement</span>
            {TOKEN_ROWS_META.map(row => (
              <div key={row.key} className={'crmsq3-tokrow' + (tokenRows[row.key].enabled ? ' crmsq3-tokrow--on' : '')}>
                <label>
                  <input type="checkbox" className="crmsq3-check" checked={tokenRows[row.key].enabled}
                         onChange={e => setTokenRows(prev => ({ ...prev, [row.key]: { ...prev[row.key], enabled: e.target.checked } }))} />
                  <div><div className="crmsq3-tokrow-tok">{row.label}</div><div className="crmsq3-tokrow-sub">{row.sub}</div></div>
                </label>
                {tokenRows[row.key].enabled && tokenAction === 'insert' && (
                  <select className="crmsq3-modal-sel" value={tokenRows[row.key].strategy}
                          onChange={e => setTokenRows(prev => ({ ...prev, [row.key]: { ...prev[row.key], strategy: e.target.value } }))}>
                    {(tokenModal === 'email' ? EMAIL_STRATEGIES : LI_STRATEGIES).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
          {tokenResult && (
            <div className="crmsq3-result">
              <div>{tokenResult.requested} contacts targeted</div>
              {tokenResult.perToken.map(r => <div key={r.token}>{'{{' + r.token + '}}'}: {r.modified} sequences updated{r.placed ? `, ${r.placed} links placed` : ''}{r.skipped ? `, ${r.skipped} steps left unchanged (no natural sentence passed the checks)` : ''}</div>)}
            </div>
          )}
        </Modal>
      )}

      {campaignOpen && (
        <Modal title="Push to campaign" onClose={() => setCampaignOpen(false)} busy={pushingToCampaign}
          footer={<>
            <button className="crmsq3-btn crmsq3-btn--ghost" onClick={() => setCampaignOpen(false)} disabled={pushingToCampaign}>Cancel</button>
            <button className="crmsq3-btn" onClick={pushToCampaign} disabled={pushingToCampaign || !ceCampaign || pushable.length === 0}>{pushingToCampaign ? 'Enrolling\u2026' : `Enrol ${pushable.length}`}</button>
          </>}>
          <p className="crmsq3-modal-s">Enrols each selected contact that has a sequence. Enrolling also approves the sequence.</p>
          {(pushHeldOutside > 0 || pushNoSeq > 0 || pushNoFit > 0) && (
            <p className="crmsq3-modal-note">
              {[
                pushHeldOutside ? `${pushHeldOutside} outside core ICP not approved yet. Approve them first, then push.` : null,
                pushNoSeq ? `${pushNoSeq} with no sequence yet.` : null,
                pushNoFit ? `${pushNoFit} marked not a fit.` : null,
              ].filter(Boolean).join(' ')}
            </p>
          )}
          <span className="crmsq3-modal-lbl">Campaign</span>
          <select className="crmsq3-modal-sel" value={ceCampaign} onChange={e => setCeCampaign(e.target.value)}>
            <option value="">Choose a campaign</option>
            {ceCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {ceCampaigns.length === 0 && <p className="crmsq3-modal-note">No campaigns in this workspace yet.</p>}
        </Modal>
      )}

      <ConfirmActionModal
        open={confirmGenerateOpen}
        title={`Generate ${pendingGenerateCount} sequence${pendingGenerateCount === 1 ? '' : 's'}?`}
        body={`This generates ${pendingGenerateCount} AI sequence${pendingGenerateCount === 1 ? '' : 's'} on the server, ${GEN_CONCURRENCY} at a time, so you can close this tab while it runs. It uses credits. Research older than 30 days isn't refreshed during bulk runs, so run Enrich on the Contacts screen first if you want fresh research.`}
        confirmLabel={`Generate ${pendingGenerateCount}`}
        onConfirm={doGenerate}
        onCancel={() => setConfirmGenerateOpen(false)}
      />
    </div>
  );
}

function Modal({ title, onClose, busy, children, footer }) {
  return (
    <div className="crmsq3-ov" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="crmsq3-modal">
        <div className="crmsq3-modal-h">
          <h3>{title}</h3>
          <button className="crmsq3-modal-x" aria-label="Close" onClick={() => { if (!busy) onClose(); }}>{'\u00D7'}</button>
        </div>
        <div className="crmsq3-modal-b">{children}</div>
        <div className="crmsq3-modal-f">{footer}</div>
      </div>
    </div>
  );
}
